/**
 * Import d'un planning Excel — la lecture d'une cellule de début de journée (§2 et §2 bis).
 *
 * Les sept règles, et l'ordre dans lequel elles sont réellement éprouvées. Cet ordre n'est PAS celui de
 * l'énoncé : le chiffre en tête (règle 5) passe avant tout autre traitement du texte. Sans cela,
 * « 1 SECURITE 18H30-02H » satisfait d'abord la règle 4 et une vacation de renfort extérieur devient un
 * créneau de service attribué à un serveur — exactement le défaut que le §2 bis corrige.
 */
import type { LectureCellule, CodeAbsence } from './types';

/** Le motif d'un horaire écrit en toutes lettres : `14H-0H30`, `18h30 - 2h`, `10H30 à 16H30`. */
const MOTIF_HORAIRE = /(\d{1,2})\s*H\s*(\d{2})?\s*[-–àa]\s*(\d{1,2})\s*H\s*(\d{2})?/i;

/**
 * Un effectif de renfort extérieur : un nombre, une espace, puis autre chose qu'un chiffre.
 * « 1 SECURITE 18H30-02H », « 4 koulibaly 15h-1h », « 3 LBE ». Jamais un horaire de salarié.
 */
const MOTIF_RENFORT = /^\s*\d+\s+\D/;

/**
 * Ce qui a le droit de devenir un code d'absence créé à la volée (règle 3), arbitré avec Emeni le
 * 2026-09-18 : aucun chiffre, 24 caractères au plus, et rien d'autre que des lettres, espaces, tirets,
 * barres obliques et apostrophes.
 *
 * Le fichier de référence mélange dans les mêmes cellules de vrais codes (`REPOS`, `congé maternité`,
 * `mise a dispo Amani`), des postes (`hotesse`, `BRASERO`, `polpo nord`), des effectifs de renfort
 * (`4 KOULI 23H`) et de la saisie sale (`(`, `,`, `9;00`, `4 (5)`). Sans ce garde-fou, l'import créerait
 * un code d'absence nommé « ( ». Les postes, eux, passent le critère et sont bien créés : non rémunérés,
 * non décomptés, listés dans le récapitulatif pour qu'Emeni les qualifie ou les supprime.
 */
const MOTIF_CODE = /^[\p{L}\s\-/']+$/u;
const LONGUEUR_CODE_MAX = 24;

/** Majuscules, accents retirés, espaces réduits — la forme sous laquelle deux codes se comparent (§4). */
export function normaliser(texte: string): string {
    return String(texte ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Une fraction de journée Excel vers des minutes depuis minuit.
 *
 * `0,375` vaut 09:00, `0,79167` vaut 19:00. Au-delà de 1, c'est le lendemain : `2,04167` se lit 01:00 du
 * matin, jamais 49 heures. Un créneau 19:00 → 01:00 doit ressortir daté sur deux jours, jamais comme une
 * durée négative ni comme trente heures.
 */
export function lireHeure(valeur: number): { minutes: number; jourSuivant: boolean } {
    const jours = Math.floor(valeur);
    const fraction = valeur - jours;
    return { minutes: Math.round(fraction * 24 * 60), jourSuivant: jours > 0 };
}

/** Des minutes depuis minuit vers `HH:MM`, en repliant sur 24 h — 01:00 du lendemain s'écrit `01:00`. */
export function formaterHeure(minutes: number): string {
    const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** L'horaire porté par un texte libre, quand il y en a un. `SECURITE 14H-0H30` donne 14:00 et 00:30. */
export function horaireDuTexte(texte: string): { debut: string; fin: string } | null {
    const trouve = MOTIF_HORAIRE.exec(String(texte ?? ''));
    if (!trouve) return null;
    const heureDebut = Number(trouve[1]);
    const heureFin = Number(trouve[3]);
    if (!Number.isFinite(heureDebut) || !Number.isFinite(heureFin)) return null;
    if (heureDebut > 23 || heureFin > 23) return null;
    return {
        debut: formaterHeure(heureDebut * 60 + Number(trouve[2] || 0)),
        fin: formaterHeure(heureFin * 60 + Number(trouve[4] || 0)),
    };
}

/** Vrai quand la cellule annonce un effectif de renfort extérieur (règle 5). */
export function estRenfort(texte: string): boolean {
    return MOTIF_RENFORT.test(String(texte ?? ''));
}

/**
 * Ce qu'un renfort relevé propose, sans rien deviner : « 1 SECURITE 18H30-02H » donne un effectif de 1,
 * le libellé SECURITE et l'horaire 18:30 → 02:00. Quand l'horaire manque ou ne se lit pas, `horaire`
 * vaut null et c'est à l'utilisateur de le saisir avant de pouvoir créer le renfort.
 */
export function lireRenfort(texte: string): { effectif: number; libelle: string; horaire: { debut: string; fin: string } | null } {
    const brut = String(texte ?? '').trim();
    const effectif = Math.max(1, parseInt(brut, 10) || 1);
    const horaire = horaireDuTexte(brut);
    // Le libellé : ce qui reste une fois l'effectif et l'horaire retirés, sans la ponctuation de bord.
    const libelle = brut
        .replace(/^\s*\d+\s+/, '')
        .replace(MOTIF_HORAIRE, ' ')
        .replace(/^[\s\-–:,/]+|[\s\-–:,/]+$/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
    return { effectif, libelle: libelle || 'Renfort', horaire };
}

/** Vrai quand le texte a la forme d'un code d'absence et peut donc être créé à la volée (règle 3). */
export function ressembleAUnCode(texte: string): boolean {
    const brut = String(texte ?? '').trim();
    if (!brut || brut.length > LONGUEUR_CODE_MAX) return false;
    return MOTIF_CODE.test(brut);
}

/**
 * Lit une cellule de début de journée, et la cellule de fin qui la suit immédiatement.
 *
 * `codesConnus` porte les codes déjà déclarés dans les réglages, sous leur forme normalisée. Un code
 * absent de cette liste n'est pas une erreur : il sera créé (règle 3), et l'import continue.
 */
export function lireCellule(
    debutBrut: unknown,
    finBrut: unknown,
    codesConnus: Set<string>,
): LectureCellule {
    // 7. Vide — ni repos, ni absence.
    if (debutBrut === null || debutBrut === undefined || debutBrut === '') return { genre: 'rien' };

    // 1. Deux nombres — un créneau. La fin au-delà de 1 le fait déborder sur le lendemain.
    if (typeof debutBrut === 'number') {
        if (typeof finBrut !== 'number') return { genre: 'rien' };
        const debut = lireHeure(debutBrut);
        const fin = lireHeure(finBrut);
        return {
            genre: 'creneau',
            debut: formaterHeure(debut.minutes),
            fin: formaterHeure(fin.minutes),
            finLendemain: fin.jourSuivant || fin.minutes <= debut.minutes,
        };
    }

    const texte = String(debutBrut).trim();
    if (!texte) return { genre: 'rien' };

    // 5. Un chiffre en tête — un renfort extérieur. Testé avant tout le reste, sinon la règle 4 s'en
    // saisirait et l'effectif deviendrait un créneau de salarié.
    if (estRenfort(texte)) return { genre: 'renfort', texte };

    const normalise = normaliser(texte);

    // 2. Un code connu — l'absence correspondante, sur ce jour-là.
    if (codesConnus.has(normalise)) {
        return { genre: 'absence', code: normalise, libelle: texte, inconnu: false };
    }

    // 4. Du texte avec un horaire, sur la ligne d'un salarié reconnu — un créneau, texte gardé en note.
    // (L'appelant n'invoque cette fonction que pour les lignes reconnues : une ligne dont le nom n'est
    // pas rapproché est écartée entière, en amont.)
    const horaire = horaireDuTexte(texte);
    if (horaire) {
        return { genre: 'creneau', debut: horaire.debut, fin: horaire.fin, finLendemain: horaire.fin <= horaire.debut, note: texte };
    }

    // 3. Un code inconnu — créé avec le libellé tel qu'écrit, puis rattaché.
    if (ressembleAUnCode(texte)) {
        return { genre: 'absence', code: normalise, libelle: texte, inconnu: true };
    }

    // 6. Du texte sans horaire — conservé en note, pour que rien ne se perde.
    return { genre: 'note', texte };
}

/** Les codes des réglages, sous la forme normalisée qui sert à les comparer. */
export function indexerCodes(codes: CodeAbsence[]): Set<string> {
    return new Set((codes || []).map((c) => normaliser(c?.code || '')).filter(Boolean));
}
